import React, { useId, useMemo } from 'react';
import { ROUTE_STRING } from 'constants/RouteString';
import { Box, Breadcrumbs, Typography } from '@mui/material';
import { Link, useParams } from 'react-router-dom';
import useAppDetailQuery from '../hooks/useAppDetailQuery';
import startCase from 'lodash/startCase';
import { ACTION_MODE_ENUM } from 'constants/AppEnums';
import { APP_CONNECTIVITY_STATE_ENUM, APP_TYPE_ENUM, COMPANY_PRODUCT_SELECT } from '@tenant/utility/constants/enum';
import { toast } from 'react-toastify';
import AppsContainer from '@tenant/core/AppsContainer';
import Steps from 'pages/publishApp/components/Steps';
import { List } from 'react-content-loader';
import checkIsEmptyGuid from 'helpers/checkIsEmptyGuid';
import { useUpsertAppPricing } from 'pages/listApp/hooks/useAppPricing';
import useEditDescription from '../hooks/useEditDescription';
import useEditGeneral from '../hooks/useEditGeneral';
import useEditConfiguration from '../hooks/useEditConfiguration';
import { STEPS_CONFIG } from 'pages/publishApp/components/Steps/constants';

const EditApp = () => {
	const { appId } = useParams();
	const uniqueId = useId();
	const { data, isLoading: loading, refetch } = useAppDetailQuery({ appId, uniqueId });

	const { mutate: mutateDescription, isPending: isPendingDescription } = useEditDescription();
	const { mutate: mutateGeneral, isPending: isPendingGeneral } = useEditGeneral();
	const { mutate: mutateConfiguration, isPending: isPendingConfiguration } = useEditConfiguration();

	const { mutate: mutatePricing, isPending: isPendingPricing, isLoading: isLoadingPricing } = useUpsertAppPricing();

	const initialValues = useMemo(() => {
		const reverseTransformMetaData = data => {
			return data.map(entry => {
				return {
					...entry,
					slots: Object.keys(entry.slots).map(slotType => {
						return {
							type: slotType,
							label: startCase(slotType),
							features: entry.slots[slotType].map(feature => ({
								...feature
							}))
						};
					})
				};
			});
		};

		return {
			...data,
			AppCode: !checkIsEmptyGuid(data?.AppCode) ? data?.AppCode : undefined,
			CategoryIds: [...new Map((data?.Categories ?? []).map(item => [item.Id, item])).values()],
			MobileBAUModuleIDs: [...new Set((data?.MobileBAUModules ?? []).map(item => item.Id))],
			MobileFeatureUrl: data?.AppConnectivityState?.toString() === APP_CONNECTIVITY_STATE_ENUM['Online'].toString() ? data?.MobileAppUrl ?? '' : '',
			LaunchType: data?.LaunchedType ?? null,
			AppType: data?.AppType ?? 1,
			AppVersionNumber: data?.AppVersion,
			AppId: data?.Id,
			PageSlots: reverseTransformMetaData(JSON.parse(data?.MetaData || '[]')),
			PageSlotsPortal: reverseTransformMetaData(JSON.parse(data?.PortalMetaData || '[]')),
			AnnuallyPrice: data?.AnnuallyPrice ?? '',
			MonthlyPrice: data?.MonthlyPrice ?? '',
			KeyLabels: data?.KeyLabels ?? '',
			AuthorName: data?.Author?.Name,
			AuthorEmail: data?.Author?.Email,
			IsPublic: !data?.IsPrivate,
			ModuleModel: data?.ModuleId ? { Id: data.ModuleId, Name: data.ModuleName ?? '' } : null,
			CompanyProducts: data?.ProductIds ? COMPANY_PRODUCT_SELECT.filter(item => data?.ProductIds?.includes(item.value)) : []
		};
	}, [data]);

	const handleSubmit = (data, { onSuccess, activeStep }) => {
		const handleSuccess = () => {
			toast.success('Edit app successfully');
			if (typeof onSuccess === 'function') {
				onSuccess();
			}
		};

		const handleError = error => {
			const message = error?.response?.data?.message || error?.message || 'Server Error';
			toast.error(message);
		};

		const actions = {
			[STEPS_CONFIG.GENERAL.key]: () => {
				debugger;
				mutateGeneral(
					{
						AppId: appId,
						Name: data.Name,
						KeyLabels: data.KeyLabels,
						Dependencies: data.Dependencies,
						CategoryIds: data.CategoryIds || data.Categories,
						PersonasIds: data.Personas?.map(item => item.Id),
						ReferralFormUrl: data.ReferralFormUrl,
						AuthorName: data.AuthorName,
						AuthorEmail: data.AuthorEmail,
						AppIconFile: data.IconBlobReference,
						CompanyProducts: data.CompanyProducts?.map(e => e.value) ?? []
					},
					{ onSuccess: handleSuccess, onError: handleError }
				);
			},
			[STEPS_CONFIG.DESCRIPTION.key]: () => {
				mutateDescription(
					{
						AppId: appId,
						MetaTitle: data.MetaTitle,
						MetaDescription: data.MetaDescription,
						ShortDescription: data.ShortDescription,
						LongDescription: data.LongDescription
					},
					{ onSuccess: handleSuccess, onError: handleError }
				);
			},
			[STEPS_CONFIG.CONFIGURATION.key]: () => {
				const formData = new FormData();
				const fields = [
					'AppId',
					'Platform',
					'LaunchType',
					'AccessScope',
					'AppUrl',
					'EmbeddedUrl',
					'PortalEmbeddedUrl',
					'AppCode',
					'MobileAppCode',
					'MobileAppUrl',
					'MobileApiUrl',
					'IsLegacy',
					'MobileIntegrationMode',
					'AppConnectivityState',
					'MetaData',
					'PortalMetaData',
					'AppVersionNumber',
					'ChangeLog',
					'Enabled',
					'MobileAppVersion',
					'MobileChangeLog'
				];

				formData.append('AppId', appId);
				formData.append('IsPrivate', !data.IsPublic);
				formData.append('IsLegacy', data.IsLegacy ?? false);

				fields.forEach(field => {
					if (data[field] !== undefined) {
						formData.append(field, data[field] ?? '');
					}
				});

				if (data?.MobileBAUModuleIDs?.length > 0) {
					data.MobileBAUModuleIDs.forEach(id => formData.append('MobileBAUModuleIDs', id));
				}

				if (data.ModuleModel?.Id) {
					formData.append('ModuleId', data.ModuleModel.Id);
					formData.append('ModuleName', data.ModuleModel.Name || '');
				}

				if (data.BundleZipFile && data?.AppConnectivityState?.toString() === APP_CONNECTIVITY_STATE_ENUM['Offline'].toString()) {
					formData.append('BundleZipFile', data.BundleZipFile);
				}

				formData.append('IntegrationMode', data.IntegrationMode || APP_TYPE_ENUM['None']);

				mutateConfiguration(formData, {
					onSuccess: () => {
						toast.success('Edit app configuration successfully');
						if (typeof onSuccess === 'function') {
							onSuccess();
						}
					},
					onError: handleError
				});
			},
			[STEPS_CONFIG.PRICING.key]: () => {
				mutatePricing({ appId, ...data }, { onSuccess: handleSuccess, onError: handleError });
			}
		};

		const action = actions[activeStep];
		if (action) {
			action();
		}
	};

	return (
		<AppsContainer
			fullView
			title={
				<Breadcrumbs aria-label="breadcrumb">
					<Typography color="text.primary">Developer</Typography>
					<Typography color="text.primary">
						<Link to={ROUTE_STRING.LIST_APP.LIST}>Apps</Link>
					</Typography>
					{appId && (
						<Typography color="text.primary">
							<Link to={ROUTE_STRING.LIST_APP.DETAIL.replace(':appId', appId)}>Detail</Link>
						</Typography>
					)}
					<Typography color="text.primary">Edit</Typography>
				</Breadcrumbs>
			}
		>
			<Box sx={{ p: 4 }}>
				{loading ? (
					<List />
				) : (
					<Steps
						mode={ACTION_MODE_ENUM['Edit']}
						input={initialValues}
						isPending={isPendingPricing || isLoadingPricing || isPendingDescription || isPendingGeneral || isPendingConfiguration || isPendingConfiguration}
						submit={handleSubmit}
						refetch={refetch}
					/>
				)}
			</Box>
		</AppsContainer>
	);
};

export default React.memo(EditApp);
